/**
 * DEX Algo Orders — one-shot smart execution across a short window.
 *
 * Distinct from DCA:
 *   - DCA          → recurring buys over days/weeks/months (set-and-forget accumulation)
 *   - Algo Orders  → ONE-SHOT execution of a large order, sliced across hours
 *                    to minimize slippage / camouflage notional from observers
 *
 * Both share the DCA engine + endpoints under the hood. Algo Orders sends
 * `executionMode: 'one_shot'` which relaxes the 1-day minimum interval check.
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Layers, Clock, CheckCircle, AlertTriangle, X, Wallet, Zap,
  Info, ExternalLink, Trash2, RefreshCw, ChevronDown, ChevronUp,
  Activity, TrendingUp, TrendingDown,
} from 'lucide-react';
import {
  useAccount, useChainId, useSignTypedData,
  usePublicClient, useWalletClient, useSwitchChain,
} from 'wagmi';
import { ConnectButton } from '@rainbow-me/rainbowkit';
import { Spinner, InfoTip } from '../components/ui';
import useTokenPrice from '../hooks/useTokenPrice';
import {
  quoteDexDcaPlan, prepareDexDcaSlice, commitDexDcaPlan,
  listDexDcaPlans, cancelDexDcaPlan,
} from '../services/api';
import { CHAINS, PROTOCOL_CHAINS, getTokensFor } from '../constants/dexTokens';
import DexCustomTokenInput from '../components/DexCustomTokenInput';

const ERC20_ABI = [
  { name: 'allowance', type: 'function', stateMutability: 'view',
    inputs: [{ name: 'owner', type: 'address' }, { name: 'spender', type: 'address' }],
    outputs: [{ type: 'uint256' }] },
  { name: 'approve', type: 'function', stateMutability: 'nonpayable',
    inputs: [{ name: 'spender', type: 'address' }, { name: 'amount', type: 'uint256' }],
    outputs: [{ type: 'bool' }] },
];
const MAX_UINT256 = 2n ** 256n - 1n;
const COW_SPENDER = '0xC92E8bdf79f0507f65a392b0ab4667716BFE0110';

// ─── Profile catalog (primary axis for Algo Orders) ─────────────────────────
const EXECUTION_PROFILES = [
  {
    id: 'twap',  label: 'TWAP',  title: 'Time-Weighted',
    desc: 'Equal-sized slices fired at regular intervals across the window. Most predictable execution.',
    icon: Activity,
  },
  {
    id: 'vwap',  label: 'VWAP',  title: 'Volume-Weighted',
    desc: 'Slice sizes follow typical hourly volume — heavier during peak liquidity, lighter overnight. Lower expected slippage on the total notional.',
    icon: TrendingUp,
  },
  {
    id: 'iceberg', label: 'Iceberg', title: 'Hidden Size',
    desc: '4× more slices at 1/4 the size each, fired at 1/4 the interval. Camouflages your total notional so observers see lots of small trades, not one large order.',
    icon: Layers,
  },
];

// ─── Time windows (short — minutes to a day) ────────────────────────────────
const TIME_WINDOWS = [
  { id: '30min', label: '30 minutes', sec: 30 * 60 },
  { id: '1h',    label: '1 hour',     sec: 60 * 60 },
  { id: '2h',    label: '2 hours',    sec: 2 * 60 * 60 },
  { id: '4h',    label: '4 hours',    sec: 4 * 60 * 60 },
  { id: '8h',    label: '8 hours',    sec: 8 * 60 * 60 },
  { id: '24h',   label: '24 hours',   sec: 24 * 60 * 60 },
];

const SLICE_COUNT_OPTIONS = [4, 6, 8, 12, 16, 20];

const SYMBOL_TO_CG = {
  WETH: 'ethereum',  ETH: 'ethereum',
  WBTC: 'bitcoin',   BTC: 'bitcoin',  cbBTC: 'bitcoin',  'BTC.b': 'bitcoin',
  WMATIC: 'matic-network', MATIC: 'matic-network', POL: 'matic-network',
  WBNB: 'binancecoin', BNB: 'binancecoin',
  WAVAX: 'avalanche-2', AVAX: 'avalanche-2',
  LINK: 'chainlink', UNI: 'uniswap', AAVE: 'aave',
  ARB: 'arbitrum', OP: 'optimism', SOL: 'solana',
};

// ─── Helpers ────────────────────────────────────────────────────────────────
function toBaseUnits(humanAmount, decimals) {
  const s = String(humanAmount).trim();
  if (!/^\d+(\.\d+)?$/.test(s)) throw new Error('amount must be a non-negative decimal');
  const [whole, frac = ''] = s.split('.');
  const padded = (frac + '0'.repeat(decimals)).slice(0, decimals);
  const combined = (whole + padded).replace(/^0+/, '') || '0';
  if (combined === '0') throw new Error('amount must be > 0');
  return combined;
}
function formatBaseUnits(baseStr, decimals, maxFrac = 4) {
  if (!baseStr || baseStr === '0') return '0';
  const s = String(baseStr).padStart(decimals + 1, '0');
  const whole = s.slice(0, -decimals).replace(/^0+/, '') || '0';
  const frac  = s.slice(-decimals).slice(0, maxFrac).replace(/0+$/, '');
  return frac ? `${whole}.${frac}` : whole;
}
function fmtTime(iso) { try { return new Date(iso).toLocaleString(); } catch { return iso; } }
function fmtUsd(n, frac = 2) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  return v.toLocaleString('en-US', { minimumFractionDigits: frac, maximumFractionDigits: frac });
}
function copyToClipboard(text) { try { navigator.clipboard?.writeText(text); } catch {} }

function Toast({ message, type = 'success', onDismiss }) {
  useEffect(() => { const t = setTimeout(onDismiss, 6000); return () => clearTimeout(t); }, [onDismiss]);
  return (
    <div className={`fixed bottom-6 right-6 z-50 px-5 py-3 rounded-xl shadow-2xl text-sm font-medium max-w-md
      ${type === 'success' ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
        : type === 'error' ? 'bg-red-500/20 text-red-300 border border-red-500/30'
        : 'bg-amber-500/20 text-amber-300 border border-amber-500/30'}`}>
      {message}
    </div>
  );
}
function Card({ children, className = '' }) {
  return <div className={`bg-dark-800 rounded-2xl p-6 border border-dark-700/50 ${className}`}>{children}</div>;
}

// ─── Profile picker (primary axis) ──────────────────────────────────────────
function ProfilePicker({ value, onChange }) {
  return (
    <div className="grid grid-cols-3 gap-2">
      {EXECUTION_PROFILES.map(p => {
        const Icon = p.icon;
        const selected = value === p.id;
        return (
          <button key={p.id} type="button" onClick={() => onChange(p.id)}
            title={p.desc}
            className={`text-left p-3 rounded-xl border transition-all ${
              selected
                ? 'bg-accent-primary/10 border-accent-primary/40 text-white shadow-[0_0_0_1px_rgba(245,158,11,0.1)]'
                : 'bg-dark-700 border-dark-600 text-gray-400 hover:bg-dark-600 hover:border-dark-500'}`}>
            <div className="flex items-center gap-2">
              <Icon className={`w-4 h-4 ${selected ? 'text-accent-primary' : 'text-gray-500'}`} />
              <span className="text-xs font-bold tracking-wider">{p.label}</span>
            </div>
            <div className="text-[10px] text-gray-500 mt-1 leading-tight">{p.title}</div>
          </button>
        );
      })}
    </div>
  );
}

// ─── Status badge ───────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const styles = {
    active:    { cls: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20', label: 'Running' },
    completed: { cls: 'bg-blue-500/10 text-blue-300 border-blue-500/20',          label: 'Completed' },
    cancelled: { cls: 'bg-dark-600 text-gray-400 border-dark-500',                label: 'Cancelled' },
  }[status] || { cls: 'bg-dark-600 text-gray-400 border-dark-500', label: status };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border ${styles.cls}
      ${status === 'active' ? 'animate-[ao-pulse_2s_ease-in-out_infinite]' : ''}`}>
      {styles.label}
    </span>
  );
}

// ─── Order row (expandable) ─────────────────────────────────────────────────
function AlgoOrderRow({ plan, onCancel, busy }) {
  const [expanded, setExpanded] = useState(false);
  const protoLabel = plan.protocol === 'cow' ? 'CoW' : plan.protocol === 'velora' ? 'Velora' : '1inch';
  const profileLabel = (plan.executionProfile || 'twap').toUpperCase();
  const profileCls = plan.executionProfile === 'vwap'
    ? 'bg-blue-500/10 text-blue-300 border-blue-500/30'
    : plan.executionProfile === 'iceberg'
      ? 'bg-violet-500/10 text-violet-300 border-violet-500/30'
      : 'bg-dark-600 text-gray-300 border-dark-500';

  const submitted = plan.schedule?.filter(s => s.status === 'submitted' || s.status === 'filled').length || 0;
  const filled    = plan.schedule?.filter(s => s.status === 'filled').length || 0;
  const failed    = plan.schedule?.filter(s => s.status === 'failed').length || 0;
  const totalSlices = plan.totalSlices || plan.schedule?.length || 0;
  const nextSlice = plan.schedule?.find(s => s.status === 'pending');

  // Window end time (last scheduled slice + small grace)
  const windowEnd = plan.schedule?.length
    ? new Date(plan.schedule[plan.schedule.length - 1].scheduledAt * 1000)
    : null;
  const totalSell = plan.totalSellAmount;

  return (
    <div className="bg-dark-700/40 rounded-xl border border-dark-600/40 overflow-hidden hover:border-dark-500/60 transition-colors">
      <button onClick={() => setExpanded(e => !e)}
        className="w-full px-4 py-3 text-left transition-colors hover:bg-dark-600/20">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <Layers className="w-3.5 h-3.5 text-accent-primary" />
              <span className="text-white text-sm font-semibold">
                {totalSell ? formatBaseUnits(totalSell, plan.sellDecimals || 6) : '—'} {plan.sellSymbol} → {plan.buySymbol}
              </span>
              <StatusBadge status={plan.status} />
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-dark-600 text-gray-400 border border-dark-500">{protoLabel}</span>
              <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold uppercase tracking-wider border ${profileCls}`}>
                {profileLabel}
              </span>
            </div>
            <p className="text-gray-500 text-xs mt-1">
              <span className="text-white">{submitted}/{totalSlices} fired</span>
              {filled > 0 && <> · <span className="text-emerald-400">{filled} filled</span></>}
              {failed > 0 && <> · <span className="text-red-400">{failed} failed</span></>}
              {nextSlice && plan.status === 'active' && (
                <> · <span className="text-amber-300"><Clock className="w-3 h-3 inline mr-0.5" />Next at {new Date(nextSlice.scheduledAt * 1000).toLocaleTimeString()}</span></>
              )}
            </p>
            {windowEnd && (
              <p className="text-gray-500 text-[11px] mt-0.5">
                Window: created {fmtTime(plan.createdAt)} → completes ~{windowEnd.toLocaleTimeString()}
              </p>
            )}
          </div>
          <div className="flex items-center gap-1 flex-shrink-0" onClick={e => e.stopPropagation()}>
            {plan.status === 'active' && (
              <button onClick={() => onCancel(plan.planId)} disabled={busy}
                title="Cancel — stops future slice submissions"
                className="p-1.5 rounded-lg bg-dark-700 text-red-400 hover:bg-red-500/10 disabled:opacity-50 transition-colors">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
            <span className="ml-1 text-gray-500">
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </span>
          </div>
        </div>
      </button>

      {expanded && (
        <div className="px-4 py-3 border-t border-dark-600/40 bg-dark-800/40 space-y-3 animate-[ao-fade_0.2s_ease-out]">
          {/* Per-slice schedule */}
          <div className="bg-dark-700/30 rounded-xl border border-dark-600 overflow-hidden">
            <div className="grid grid-cols-12 px-3 py-1.5 text-[10px] uppercase tracking-wider text-gray-500 font-semibold bg-dark-800/60">
              <div className="col-span-1">#</div>
              <div className="col-span-3">Fires at</div>
              <div className="col-span-3">Size</div>
              <div className="col-span-2">Status</div>
              <div className="col-span-3 text-right">Order</div>
            </div>
            <div className="max-h-72 overflow-y-auto">
              {plan.schedule?.map((s, idx) => {
                const stateCls = s.status === 'filled'    ? 'text-emerald-400'
                              : s.status === 'submitted' ? 'text-blue-300'
                              : s.status === 'failed'    ? 'text-red-400'
                              : s.status === 'cancelled' ? 'text-gray-500'
                              : 'text-gray-400';
                const sliceTime = new Date(Number(s.scheduledAt) * 1000).toLocaleTimeString();
                const sliceAmount = s.sellAmount || plan.sellAmountPerSlice;
                return (
                  <div key={s.sliceIndex} className={`grid grid-cols-12 px-3 py-1.5 text-xs ${idx < plan.schedule.length - 1 ? 'border-b border-dark-600/30' : ''} hover:bg-dark-600/20`}>
                    <div className="col-span-1 text-gray-500 font-mono">#{s.sliceIndex + 1}</div>
                    <div className="col-span-3 text-gray-300 font-mono">{sliceTime}</div>
                    <div className="col-span-3 text-white font-mono">{formatBaseUnits(sliceAmount, plan.sellDecimals || 6)} {plan.sellSymbol}</div>
                    <div className={`col-span-2 ${stateCls}`}>{s.status}</div>
                    <div className="col-span-3 text-right text-gray-500 font-mono truncate" title={s.orderId || '—'}>
                      {s.orderId ? `${String(s.orderId).slice(0, 6)}…` : '—'}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Plan ID + metadata */}
          <div className="bg-dark-700/40 rounded-lg px-3 py-2 space-y-1.5">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1">Algo Order ID</div>
              <button onClick={() => copyToClipboard(plan.planId)} title="Copy"
                className="text-xs font-mono text-white break-all hover:text-accent-primary transition-colors text-left">
                {plan.planId}
              </button>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs pt-1 border-t border-dark-600/40">
              <div><span className="text-gray-500">Chain:</span> <span className="text-gray-300">{plan.chainId}</span></div>
              <div><span className="text-gray-500">Pricing:</span> <span className="text-gray-300">{plan.pricingModel === 'market' ? `Market ±${plan.slippagePct}%` : `Limit @ ${plan.limitPrice}`}</span></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Create panel ───────────────────────────────────────────────────────────
function CreateAlgoOrderPanel({ onCreated, onToast }) {
  const { address: connectedAddr, isConnected } = useAccount();
  const connectedChainId = useChainId();
  const { signTypedDataAsync } = useSignTypedData();
  const publicClient = usePublicClient();
  const { data: walletClient } = useWalletClient();
  const { switchChain, isPending: switchingChain } = useSwitchChain();

  const [profile, setProfile] = useState('twap');
  const [protocol, setProtocol] = useState('cow');
  const [chainId, setChainId] = useState(connectedChainId || 1);
  const [sellSym, setSellSym] = useState('USDC');
  const [buySym,  setBuySym]  = useState('WETH');
  const [totalAmount, setTotalAmount] = useState('');
  const [windowId, setWindowId] = useState('2h');
  const [totalSlices, setTotalSlices] = useState(8);
  const [pricingModel, setPricingModel] = useState('market');
  const [slippagePct, setSlippagePct] = useState(1);
  const [limitPrice, setLimitPrice] = useState('');
  const [wizard, setWizard] = useState(null);
  const [customTick, setCustomTick] = useState(0);

  const tokens = useMemo(() => getTokensFor(chainId), [chainId, customTick]);
  const sellToken = tokens.find(t => t.symbol === sellSym);
  const buyToken  = tokens.find(t => t.symbol === buySym);
  const availableChains = CHAINS.filter(c => PROTOCOL_CHAINS[protocol].includes(c.id));
  const window = TIME_WINDOWS.find(w => w.id === windowId);

  const cgId = SYMBOL_TO_CG[buySym] || null;
  const { data: liveData } = useTokenPrice(cgId, 30000);
  const livePrice = liveData?.currentPriceUsd ?? liveData?.market_data?.current_price?.usd ?? null;

  useEffect(() => {
    const list = getTokensFor(chainId);
    if (!list.find(t => t.symbol === sellSym)) setSellSym(list[0]?.symbol || '');
    if (!list.find(t => t.symbol === buySym))  setBuySym(list[1]?.symbol || list[0]?.symbol || '');
    if (sellSym && buySym && sellSym === buySym) {
      const alt = list.find(t => t.symbol !== sellSym);
      if (alt) setBuySym(alt.symbol);
    }
  }, [chainId, sellSym, buySym]);

  useEffect(() => {
    if (!PROTOCOL_CHAINS[protocol].includes(chainId)) setChainId(PROTOCOL_CHAINS[protocol][0]);
  }, [protocol, chainId]);

  // Derived params for the live preview + backend call
  const intervalSec = useMemo(() => Math.floor(window.sec / totalSlices), [window, totalSlices]);
  const effectiveSlices = profile === 'iceberg' ? totalSlices * 4 : totalSlices;
  const effectiveInterval = profile === 'iceberg' ? Math.floor(intervalSec / 4) : intervalSec;

  // Validation: keep effective interval ≥ 60s
  const intervalTooShort = effectiveInterval < 60;

  const disabledReason = (() => {
    if (!isConnected || !connectedAddr) return 'Connect your wallet first';
    if (Number(connectedChainId) !== Number(chainId))
      return `Wallet is on chain ${connectedChainId}, not ${CHAINS.find(c => c.id === Number(chainId))?.label || chainId}`;
    if (!sellToken || !buyToken || sellToken.symbol === buyToken.symbol) return 'Sell and buy tokens must be different';
    if (!(Number(totalAmount) > 0)) return 'Enter a total amount > 0';
    if (intervalTooShort) return `Slices fire too fast (${effectiveInterval}s) — pick a longer window or fewer slices`;
    if (pricingModel === 'limit' && !(Number(limitPrice) > 0)) return 'Enter a limit price > 0';
    if (wizard) return 'Submitting…';
    return null;
  })();
  const canSubmit = disabledReason == null;

  async function ensureAllowance(spender, requiredAmount) {
    setWizard(w => ({ ...w, phase: 'checking-allowance' }));
    async function read() {
      return publicClient.readContract({
        address: sellToken.address, abi: ERC20_ABI,
        functionName: 'allowance', args: [connectedAddr, spender],
      });
    }
    let allowance = await read();
    if (allowance >= requiredAmount) return;
    if (allowance > 0n) {
      setWizard(w => ({ ...w, phase: 'approving' }));
      onToast({ type: 'info', message: `${sellSym} needs allowance reset to 0 first — confirm in wallet.` });
      await walletClient.writeContract({
        address: sellToken.address, abi: ERC20_ABI,
        functionName: 'approve', args: [spender, 0n],
      });
      const start = Date.now();
      while (Date.now() - start < 180_000) {
        await new Promise(r => setTimeout(r, 4000));
        try { allowance = await read(); } catch {}
        if (allowance === 0n) break;
      }
      if (allowance !== 0n) throw new Error('Allowance reset did not confirm');
    }
    setWizard(w => ({ ...w, phase: 'approving' }));
    onToast({ type: 'info', message: `Approve ${sellSym} once. ~15–30s for on-chain confirmation.` });
    await walletClient.writeContract({
      address: sellToken.address, abi: ERC20_ABI,
      functionName: 'approve', args: [spender, MAX_UINT256],
    });
    const start = Date.now();
    while (Date.now() - start < 180_000) {
      await new Promise(r => setTimeout(r, 4000));
      try { allowance = await read(); } catch {}
      if (allowance >= requiredAmount) return;
    }
    throw new Error('Approval did not confirm in 3 min');
  }

  async function handleSubmit() {
    if (!canSubmit) return;
    try {
      const totalBase = toBaseUnits(totalAmount, sellToken.decimals);
      const sellAmountPerSlice = (BigInt(totalBase) / BigInt(totalSlices)).toString();

      setWizard({ phase: 'quoting' });
      const quote = await quoteDexDcaPlan({
        protocol, chainId,
        owner: connectedAddr,
        sellToken: sellToken.address, buyToken: buyToken.address,
        sellSymbol: sellToken.symbol, buySymbol: buyToken.symbol,
        sellDecimals: sellToken.decimals, buyDecimals: buyToken.decimals,
        sellAmountPerSlice,
        pricingModel,
        slippagePct: pricingModel === 'market' ? Number(slippagePct) : undefined,
        limitPrice:  pricingModel === 'limit'  ? String(limitPrice)   : undefined,
        intervalSec,
        totalSlices,
        executionProfile: profile,
        executionMode: 'one_shot',
      });
      const { planId, schedule, perSliceTakingAmount, effectiveSlices: effSlicesFromBackend } = quote.data;
      const sliceCount = effSlicesFromBackend || schedule.length;

      let resolvedSpender = protocol === 'cow' ? COW_SPENDER : null;
      let approved = false;
      async function approveOnceWeKnowSpender() {
        if (approved || !resolvedSpender) return;
        await ensureAllowance(resolvedSpender, BigInt(totalBase));
        approved = true;
      }
      await approveOnceWeKnowSpender();

      const signedSlices = [];
      for (let i = 0; i < sliceCount; i++) {
        const slot = schedule[i];
        const sliceSellAmount   = slot.sellAmount   || sellAmountPerSlice;
        const sliceTakingAmount = slot.takingAmount || perSliceTakingAmount;
        const prep = await prepareDexDcaSlice({
          protocol, chainId,
          maker: connectedAddr,
          makerAsset: sellToken.address, takerAsset: buyToken.address,
          makingAmount: sliceSellAmount, takingAmount: sliceTakingAmount,
          sliceIndex: slot.sliceIndex, scheduledAt: slot.scheduledAt,
          srcDecimals: sellToken.decimals, destDecimals: buyToken.decimals,
        });
        if (!resolvedSpender && prep.data.spender) resolvedSpender = prep.data.spender;
        await approveOnceWeKnowSpender();

        setWizard({ phase: 'signing', signedSoFar: i, sliceCount });
        const { typedData, order } = prep.data;
        const signature = await signTypedDataAsync({
          domain: typedData.domain, types: typedData.types,
          primaryType: typedData.primaryType, message: order,
        });
        signedSlices.push({
          sliceIndex: slot.sliceIndex,
          scheduledAt: slot.scheduledAt,
          sellAmount: sliceSellAmount,
          weight: slot.weight,
          order, signature,
          orderId: prep.data.orderHash || prep.data.orderUid || null,
        });
      }

      setWizard({ phase: 'committing', sliceCount });
      const commit = await commitDexDcaPlan({
        planId, protocol, chainId, owner: connectedAddr,
        sellToken: sellToken.address, buyToken: buyToken.address,
        sellSymbol: sellToken.symbol, buySymbol: buyToken.symbol,
        sellDecimals: sellToken.decimals, buyDecimals: buyToken.decimals,
        sellAmountPerSlice,
        takingAmountPerSlice: perSliceTakingAmount,
        pricingModel,
        slippagePct: pricingModel === 'market' ? Number(slippagePct) : undefined,
        limitPrice: pricingModel === 'limit' ? String(limitPrice) : undefined,
        intervalSec, totalSlices: sliceCount,
        executionProfile: profile,
        executionMode: 'one_shot',
        slices: signedSlices,
      });

      const profileLabel = profile === 'iceberg' ? `Iceberg (${sliceCount} mini-slices)`
                        : profile === 'vwap'    ? `VWAP-weighted ${sliceCount} slices`
                        : `${sliceCount} TWAP slices`;
      onToast({ type: 'success', message: `Algo Order armed — ${profileLabel} firing across ${window.label}.` });
      setTotalAmount(''); setLimitPrice('');
      onCreated(commit.data?.plan);
      setWizard(null);
    } catch (err) {
      const msg = err?.shortMessage || err?.message || 'Could not create algo order';
      onToast({ type: 'error', message: msg });
      setWizard(null);
    }
  }

  const wizardLabel = (() => {
    if (!wizard) return 'Execute order';
    if (wizard.phase === 'quoting') return 'Computing slice plan…';
    if (wizard.phase === 'checking-allowance') return 'Checking allowance…';
    if (wizard.phase === 'approving') return `Approving ${sellSym}…`;
    if (wizard.phase === 'signing') return `Sign ${wizard.signedSoFar + 1} of ${wizard.sliceCount}…`;
    if (wizard.phase === 'committing') return 'Saving order…';
    return 'Working…';
  })();

  return (
    <Card>
      <div className="flex items-center gap-2 mb-4">
        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-accent-primary/20 to-accent-primary/5 text-accent-primary flex items-center justify-center border border-accent-primary/20">
          <Layers className="w-4 h-4" />
        </div>
        <div>
          <h3 className="text-white font-semibold text-sm">New Algo Order</h3>
          <p className="text-gray-500 text-xs">Execute one large order across a short window — TWAP / VWAP / Iceberg, sliced for minimum market impact.</p>
        </div>
      </div>

      {!isConnected && (
        <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl px-4 py-3 mb-4 space-y-3">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
            <p className="text-amber-300 text-sm font-semibold">Connect your wallet first</p>
          </div>
          <ConnectButton.Custom>
            {({ openConnectModal }) => (
              <button onClick={openConnectModal}
                className="w-full py-2.5 rounded-xl text-sm font-bold bg-gradient-to-r from-amber-500 to-amber-600 text-white hover:from-amber-400 hover:to-amber-500 flex items-center justify-center gap-2">
                <Wallet className="w-4 h-4" /> Connect Wallet
              </button>
            )}
          </ConnectButton.Custom>
        </div>
      )}
      {isConnected && connectedAddr && (
        <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-xl px-4 py-2 mb-4 flex items-center gap-2 text-xs">
          <CheckCircle className="w-3.5 h-3.5 text-emerald-400" />
          <span className="text-emerald-300">Connected as <code className="font-mono">{connectedAddr.slice(0,6)}…{connectedAddr.slice(-4)}</code></span>
        </div>
      )}
      {isConnected && Number(connectedChainId) !== Number(chainId) && (
        <div className="bg-blue-500/5 border border-blue-500/20 rounded-xl px-4 py-3 mb-4 space-y-3">
          <div className="flex items-start gap-3">
            <Info className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
            <p className="text-blue-300 text-xs">Wallet is on chain {connectedChainId}; switch to {CHAINS.find(c => c.id === chainId)?.label}.</p>
          </div>
          <button onClick={() => switchChain?.({ chainId: Number(chainId) })} disabled={switchingChain || !switchChain}
            className="w-full py-2.5 rounded-xl text-sm font-bold bg-blue-500 hover:bg-blue-400 text-white disabled:opacity-40">
            {switchingChain ? 'Switching…' : `Switch to ${CHAINS.find(c => c.id === Number(chainId))?.label}`}
          </button>
        </div>
      )}

      <div className="space-y-4">
        {/* Execution profile — primary axis */}
        <div>
          <label className="text-gray-500 text-xs uppercase tracking-wide mb-2 flex items-center justify-between">
            <span className="flex items-center gap-1">
              Execution profile
              <InfoTip side="right">TWAP = equal slices, predictable. VWAP = volume-weighted (heavier at peak liquidity hours). Iceberg = 4× more slices at 1/4 size each, hides total notional from observers.</InfoTip>
            </span>
            <span className="text-gray-600 normal-case text-[10px]">{EXECUTION_PROFILES.find(p => p.id === profile)?.desc}</span>
          </label>
          <ProfilePicker value={profile} onChange={setProfile} />
        </div>

        {/* Protocol */}
        <div>
          <label className="text-gray-500 text-xs uppercase tracking-wide mb-1.5 block">Protocol</label>
          <div className="grid grid-cols-2 gap-2">
            {['cow', 'velora'].map(p => (
              <button key={p} type="button" onClick={() => setProtocol(p)}
                className={`px-3 py-2 rounded-xl text-xs font-medium border transition-all ${
                  protocol === p ? 'bg-accent-primary/10 border-accent-primary/40 text-white' : 'bg-dark-700 border-dark-600 text-gray-400 hover:bg-dark-600'}`}>
                {p === 'cow' ? 'CoW' : 'Velora'}
              </button>
            ))}
          </div>
        </div>

        {/* Chain */}
        <div>
          <label className="text-gray-500 text-xs uppercase tracking-wide mb-1.5 block">Network</label>
          <select value={chainId} onChange={e => setChainId(Number(e.target.value))}
            className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-accent-primary">
            {availableChains.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
        </div>

        {/* Pair */}
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="text-gray-500 text-xs uppercase tracking-wide mb-1.5 block">Spend</label>
            <select value={sellSym} onChange={e => setSellSym(e.target.value)}
              className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-accent-primary">
              {tokens.map(t => <option key={t.address} value={t.symbol}>{t.symbol}</option>)}
            </select>
          </div>
          <div>
            <label className="text-gray-500 text-xs uppercase tracking-wide mb-1.5 flex items-center gap-1">
              Total amount
              <InfoTip side="top">Full size you want to swap. Per-slice amount auto-computes as total / slice count. Algo Orders are gas-efficient for $5k+ trades where one-shot would have meaningful price impact.</InfoTip>
            </label>
            <input type="number" min="0" step="any" value={totalAmount}
              onChange={e => setTotalAmount(e.target.value)} placeholder="0.0"
              className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-accent-primary placeholder-gray-600" />
          </div>
          <div>
            <label className="text-gray-500 text-xs uppercase tracking-wide mb-1.5 block">Trade</label>
            <select value={buySym} onChange={e => setBuySym(e.target.value)}
              className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-accent-primary">
              {tokens.filter(t => t.symbol !== sellSym).map(t => <option key={t.address} value={t.symbol}>{t.symbol}</option>)}
            </select>
          </div>
        </div>

        <DexCustomTokenInput chainId={chainId}
          onResolved={(tok) => { setCustomTick(t => t + 1); setBuySym(tok.symbol); }} />

        {/* Window + Slices */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-gray-500 text-xs uppercase tracking-wide mb-1.5 flex items-center gap-1">
              Time window
              <InfoTip side="top">Total duration to spread your order across. Algo Orders shine at 4h+. Under 1h, just use DEX Swap one-shot — not enough time for the slicing to reduce price impact.</InfoTip>
            </label>
            <select value={windowId} onChange={e => setWindowId(e.target.value)}
              className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-accent-primary">
              {TIME_WINDOWS.map(w => <option key={w.id} value={w.id}>{w.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-gray-500 text-xs uppercase tracking-wide mb-1.5 block">Slices</label>
            <select value={totalSlices} onChange={e => setTotalSlices(Number(e.target.value))}
              className="w-full bg-dark-700 border border-dark-600 rounded-xl px-3 py-2.5 text-white text-sm focus:outline-none focus:border-accent-primary">
              {SLICE_COUNT_OPTIONS.map(n => <option key={n} value={n}>{n} slices</option>)}
            </select>
          </div>
        </div>

        {/* Pricing model */}
        <div>
          <label className="text-gray-500 text-xs uppercase tracking-wide mb-1.5 block">Pricing</label>
          <div className="grid grid-cols-2 gap-2 mb-2">
            <button onClick={() => setPricingModel('market')}
              className={`px-3 py-2 rounded-xl text-xs font-medium transition-colors border ${
                pricingModel === 'market' ? 'bg-accent-primary/10 border-accent-primary/40 text-white' : 'bg-dark-700 border-dark-600 text-gray-400 hover:bg-dark-600'}`}>
              Market ± slippage
            </button>
            <button onClick={() => setPricingModel('limit')}
              className={`px-3 py-2 rounded-xl text-xs font-medium transition-colors border ${
                pricingModel === 'limit' ? 'bg-accent-primary/10 border-accent-primary/40 text-white' : 'bg-dark-700 border-dark-600 text-gray-400 hover:bg-dark-600'}`}>
              Fixed limit price
            </button>
          </div>
          {pricingModel === 'market' ? (
            <div className="flex items-center gap-2">
              <span className="text-gray-500 text-xs">Slippage:</span>
              <input type="number" min="0.1" max="10" step="0.1" value={slippagePct}
                onChange={e => setSlippagePct(e.target.value)}
                className="w-20 bg-dark-700 border border-dark-600 rounded-lg px-2 py-1 text-white text-xs text-center focus:outline-none focus:border-accent-primary" />
              <span className="text-gray-500 text-xs">%</span>
              {livePrice && <span className="text-gray-500 text-[11px] ml-2">Live {buySym}: ${fmtUsd(livePrice, livePrice >= 1 ? 2 : 4)}</span>}
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <span className="text-gray-500 text-xs">{buySym} per {sellSym}:</span>
              <input type="number" min="0" step="any" value={limitPrice}
                onChange={e => setLimitPrice(e.target.value)} placeholder="0.0"
                className="flex-1 bg-dark-700 border border-dark-600 rounded-lg px-2 py-1 text-white text-xs focus:outline-none focus:border-accent-primary" />
            </div>
          )}
        </div>

        {/* Plan summary */}
        {totalAmount && Number(totalAmount) > 0 && (
          <div className="bg-dark-700/40 rounded-xl px-4 py-3 text-xs space-y-1">
            <div className="flex justify-between"><span className="text-gray-500">Total</span><span className="text-white font-semibold">{totalAmount} {sellSym}</span></div>
            <div className="flex justify-between">
              <span className="text-gray-500">Slices</span>
              <span className="text-white">
                {effectiveSlices} × {profile === 'iceberg' ? (Number(totalAmount) / effectiveSlices).toFixed(4) : (Number(totalAmount) / totalSlices).toFixed(4)} {sellSym}
                {profile === 'vwap' && <span className="text-gray-500"> (volume-weighted)</span>}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Cadence</span>
              <span className="text-white">every {effectiveInterval < 60 ? `${effectiveInterval}s` : `${Math.round(effectiveInterval / 60)}min`}</span>
            </div>
            <div className="flex justify-between"><span className="text-gray-500">Signatures</span><span className="text-white">{effectiveSlices} (one per slice)</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Total window</span><span className="text-white">{window.label}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">Profile</span><span className="text-accent-primary font-bold uppercase tracking-wider text-[10px]">{profile}</span></div>
          </div>
        )}

        <button onClick={handleSubmit} disabled={!canSubmit}
          className={`relative w-full py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed transition-all overflow-hidden
            ${canSubmit
              ? 'bg-gradient-to-r from-accent-primary to-amber-500 text-dark-900 shadow-lg shadow-accent-primary/20 hover:shadow-xl hover:shadow-accent-primary/30 hover:-translate-y-px'
              : 'bg-dark-700 text-gray-500'}`}>
          {wizard ? <Spinner size="sm" /> : <Layers className="w-4 h-4" />}
          {wizardLabel}
        </button>
        {disabledReason ? (
          <p className="text-amber-400 text-[11px] text-center flex items-center justify-center gap-1">
            <AlertTriangle className="w-3 h-3" /> {disabledReason}
          </p>
        ) : (
          <p className="text-gray-500 text-[11px] text-center">
            One approval + {effectiveSlices} signatures. After that, the order executes autonomously across {window.label}.
          </p>
        )}
      </div>
    </Card>
  );
}

// ─── Page ───────────────────────────────────────────────────────────────────
export default function DexAlgoOrdersPage() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [busy, setBusy] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await listDexDcaPlans();
      // Only show plans created via Algo Orders (executionMode === 'one_shot').
      // Recurring DCA plans live on the /dex-dca page and are filtered out here.
      const all = r.data?.plans || [];
      setOrders(all.filter(p => p.executionMode === 'one_shot'));
    } catch (err) {
      setToast({ type: 'error', message: err?.message || 'Could not load algo orders' });
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const handleCreated = (plan) => { if (plan) reload(); };
  const handleCancel = async (planId) => {
    if (!window.confirm('Cancel this algo order? Slices that have already submitted to the orderbook will continue toward their fills; remaining pending slices will be stopped.')) return;
    setBusy(planId);
    try {
      await cancelDexDcaPlan(planId);
      await reload();
      setToast({ type: 'success', message: 'Algo order cancelled. Pending slices stopped.' });
    } catch (err) {
      setToast({ type: 'error', message: err?.message || 'Cancel failed' });
    } finally { setBusy(null); }
  };

  const active = orders.filter(o => o.status === 'active');
  const history = orders.filter(o => o.status !== 'active');

  return (
    <div className="space-y-6">
      <style>{`
        @keyframes ao-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.65; } }
        @keyframes ao-fade { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
      <div>
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-accent-primary/25 to-accent-primary/5 text-accent-primary flex items-center justify-center border border-accent-primary/20">
            <Layers className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">DEX Algo Orders</h1>
            <p className="text-gray-400 text-sm">Execute one large order across a short window. TWAP / VWAP / Iceberg slicing reduces market impact and camouflages your intent.</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <CreateAlgoOrderPanel onCreated={handleCreated} onToast={setToast} />

        <Card>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-white font-semibold text-sm">Active algo orders</h3>
              <p className="text-gray-500 text-xs">{active.length} running</p>
            </div>
            <button onClick={reload} disabled={loading}
              className="btn-ghost text-xs px-3 py-1.5 flex items-center gap-1.5">
              <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} /> Reload
            </button>
          </div>
          {loading ? (
            <div className="flex justify-center py-10"><Spinner /></div>
          ) : active.length === 0 ? (
            <div className="text-center py-10">
              <Layers className="w-8 h-8 text-gray-600 mx-auto mb-2" />
              <p className="text-gray-500 text-sm">No active algo orders. Pick a profile and execute one large order intelligently.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {active.map(o => (
                <AlgoOrderRow key={o.planId} plan={o} onCancel={handleCancel} busy={busy === o.planId} />
              ))}
            </div>
          )}
        </Card>
      </div>

      {history.length > 0 && (
        <Card>
          <h3 className="text-white font-semibold text-sm mb-3">History</h3>
          <div className="space-y-2">
            {history.map(o => (
              <AlgoOrderRow key={o.planId} plan={o} onCancel={handleCancel} busy={busy === o.planId} />
            ))}
          </div>
        </Card>
      )}

      {toast && <Toast {...toast} onDismiss={() => setToast(null)} />}
    </div>
  );
}
